import { Dispatch, SetStateAction, useCallback, useRef } from 'react';

import { useFullStory } from '@goshippo/utilities';

import { throttle } from 'lodash';

import { useAuthentication } from './useAuthentication';
import { usePortalFullStoryFeatureFlags } from './usePortalFullStoryFeatureFlags';
import { useUserAccount } from './useUserAccount';

type UserVariables = Record<string, unknown>;
export type SetUserVariables = Dispatch<SetStateAction<UserVariables>>;

const THROTTLE_TIME = 5000;

/**
 * Forwards user vars to FullStory
 */
export const usePortalFullStoryUserVars = () => {
  const { isInitialized, setUserVars } = useFullStory();
  const { data: user } = useUserAccount();
  const { isImpersonating } = useAuthentication();
  const pendingUserVariables = useRef<UserVariables>({});

  const fullStoryIsInitialized = isInitialized();

  const flushpendingUserVariables = useRef(
    throttle(() => {
      setUserVars({ ...pendingUserVariables.current });
      pendingUserVariables.current = {};
    }, THROTTLE_TIME),
  );

  const setUserVariables = useCallback(
    (argument) => {
      if (!fullStoryIsInitialized) return;
      if (!user) return;
      if (isImpersonating) return;

      if (argument instanceof Function) {
        pendingUserVariables.current = argument(pendingUserVariables.current);
      } else {
        pendingUserVariables.current = {
          ...pendingUserVariables.current,
          ...argument,
        };
      }

      flushpendingUserVariables.current();
    },
    [fullStoryIsInitialized, isImpersonating, user],
  );

  usePortalFullStoryFeatureFlags({ setUserVariables });
};
